/**
 * momentPrefetcher — the Snap model for receiving (FEAT-028 wave 6).
 *
 * Bytes are already on the phone when the finger lands: the inbox and the
 * conversation screen queue unopened moments' media here, so the viewers open
 * from memory instead of the network. Safe by construction — GET
 * /messages/{id}/media records NOTHING (FEAT-024's split), an unopened moment
 * has no death clock, and re-serving is idempotent (BUG-061). This service
 * must never touch the legacy /view or /replay endpoints: those record.
 *
 * It stores Blobs, never object URLs. That is what makes a module singleton
 * safe here where blobLru.ts forbids one: ProtectedRoute double-mounts pages,
 * and the hazard is one mount revoking URLs the other displays — a Blob store
 * revokes nothing. Viewers mint their own URLs from these Blobs in their own
 * instance-scoped BlobLru.
 *
 * Concurrency is capped at 2 on purpose: every media GET costs the server a
 * full-file decrypt, and media routes have no rate limit — this cap is the
 * only throttle.
 */

import { API_BASE_URL } from '../api/client';

interface CacheEntry {
  blob: Blob;
  timestamp: number;
}

interface QueueItem {
  key: string;
  url: string;
  priority: number;
  resolve: (blob: Blob | null) => void;
}

const MAX_CONCURRENT = 2;
const MAX_ENTRIES = 16;
const CACHE_TTL = 5 * 60 * 1000;
/** Per kickoff — a conversation with 40 unread moments warms the first 10. */
const MAX_PER_KICKOFF = 10;
/**
 * FEAT-028 wave 7: the cache is budgeted in BYTES, not entries — 16 4K clips
 * would be well over a gigabyte of Blobs on a phone. Videos past the
 * per-item line are not warmed at all: the wave-6 streamed path carries a
 * cold tap to first frame in seconds, so warming 90 MB buys almost nothing
 * and costs the person's data plan. Photos always warm (server caps ~3 MB).
 */
const BYTE_BUDGET = 150 * 1024 * 1024;
const VIDEO_PREFETCH_MAX_BYTES = 15 * 1024 * 1024;
/** Cumulative KNOWN bytes one kickoff may queue — bounds cell-data burn. */
const KICKOFF_BYTE_CAP = 60 * 1024 * 1024;

export interface PrefetchableMoment {
  id: number;
  media_type?: string | null;
  /** Delivery size — null/absent on pre-wave-7 rows (treated as small). */
  media_size_bytes?: number | null;
}

class MomentPrefetcherService {
  private cache = new Map<string, CacheEntry>();
  private cacheBytes = 0;
  private pending = new Map<string, Promise<Blob | null>>();
  private queue: QueueItem[] = [];
  private active = 0;

  /** Warm the cache for a run of unopened moments. Best-effort, silent. */
  prefetchMoments(moments: PrefetchableMoment[]): void {
    const batch = moments.slice(0, MAX_PER_KICKOFF);
    if (batch.length === 0) return;
    console.info(`[MomentPrefetch] warming ${batch.length} moment(s)`);
    let queuedBytes = 0;
    for (const moment of batch) {
      // The 600px poster paints first frames on iOS (BUG-093 cashed in) —
      // small, so it outranks the media bytes in the queue.
      if (moment.media_type === 'video') {
        void this.fetchThumb(moment.id, 1);
      }
      // Byte policy (wave 7): a video past the per-item line rides the
      // streamed path instead of warming; null size = pre-wave row, small by
      // era. Posters are excluded from the kickoff byte cap — they are the
      // cheap thing this service exists to have ready.
      const size = moment.media_size_bytes ?? null;
      if (
        moment.media_type === 'video' &&
        size !== null &&
        size > VIDEO_PREFETCH_MAX_BYTES
      ) {
        console.info(
          `[MomentPrefetch] skip media:${moment.id} (${size} bytes > per-item line)`
        );
        continue;
      }
      if (size !== null && queuedBytes + size > KICKOFF_BYTE_CAP) {
        console.info('[MomentPrefetch] kickoff byte cap reached');
        break;
      }
      queuedBytes += size ?? 0;
      void this.fetchMedia(moment.id, 0);
    }
  }

  /**
   * The media bytes for a moment — cached, deduped, queued.
   *
   * `immediate` is for a USER TAP: it starts the fetch right now, past the
   * background concurrency cap, and yanks the key out of the queue if it was
   * waiting there. A tap must never sit behind two slow background
   * prefetches — the cap protects the server from the warm-up, not from the
   * person.
   */
  fetchMedia(id: number, priority = 2, immediate = false): Promise<Blob | null> {
    return this.fetch(
      `media:${id}`,
      `${API_BASE_URL}/messages/${id}/media`,
      priority,
      immediate
    );
  }

  /** The 600px thumbnail, used as the video poster. */
  fetchThumb(id: number, priority = 3): Promise<Blob | null> {
    return this.fetch(
      `thumb:${id}`,
      `${API_BASE_URL}/messages/${id}/thumbnail`,
      priority
    );
  }

  /** Synchronous cache probe — the viewer's fast path. */
  getMedia(id: number): Blob | undefined {
    return this.get(`media:${id}`);
  }

  getThumb(id: number): Blob | undefined {
    return this.get(`thumb:${id}`);
  }

  /** The moment was opened — its clock is running, stop holding spare bytes. */
  evict(id: number): void {
    this.drop(`media:${id}`);
    this.drop(`thumb:${id}`);
  }

  /** Logout / session expiry: no cross-account leftovers, ever. */
  clearAll(): void {
    this.cache.clear();
    this.cacheBytes = 0;
    this.queue = [];
  }

  private drop(key: string): void {
    const entry = this.cache.get(key);
    if (!entry) return;
    this.cacheBytes -= entry.blob.size;
    this.cache.delete(key);
  }

  private get(key: string): Blob | undefined {
    const entry = this.cache.get(key);
    if (!entry) return undefined;
    if (Date.now() - entry.timestamp >= CACHE_TTL) {
      this.drop(key);
      return undefined;
    }
    // Refresh LRU position
    this.cache.delete(key);
    this.cache.set(key, entry);
    return entry.blob;
  }

  private fetch(
    key: string,
    url: string,
    priority: number,
    immediate = false
  ): Promise<Blob | null> {
    const cached = this.get(key);
    if (cached) return Promise.resolve(cached);

    const pending = this.pending.get(key);
    if (pending) {
      if (immediate) {
        // If the key is still QUEUED (not yet running), pull it forward —
        // its promise is already handed out, so run the same item now.
        const queuedIndex = this.queue.findIndex((q) => q.key === key);
        if (queuedIndex >= 0) {
          const [item] = this.queue.splice(queuedIndex, 1);
          this.active += 1;
          void this.run(item);
        }
      }
      return pending;
    }

    const item: QueueItem = { key, url, priority, resolve: () => {} };
    const promise = new Promise<Blob | null>((resolve) => {
      item.resolve = resolve;
    });
    this.pending.set(key, promise);
    if (immediate) {
      this.active += 1;
      void this.run(item);
    } else {
      this.queue.push(item);
      this.processQueue();
    }
    return promise;
  }

  private processQueue(): void {
    while (this.active < MAX_CONCURRENT && this.queue.length > 0) {
      this.queue.sort((a, b) => b.priority - a.priority);
      const item = this.queue.shift();
      if (!item) break;

      const cached = this.get(item.key);
      if (cached) {
        item.resolve(cached);
        this.pending.delete(item.key);
        continue;
      }

      this.active += 1;
      void this.run(item);
    }
  }

  private async run(item: QueueItem): Promise<void> {
    try {
      const response = await fetch(item.url, { credentials: 'include' });
      if (!response.ok) {
        // Expired, deleted, offline — the viewer's own fetch path owns the
        // user-facing outcome; a prefetch just quietly comes up empty.
        item.resolve(null);
        return;
      }
      const blob = await response.blob();
      this.store(item.key, blob);
      console.info(`[MomentPrefetch] cached ${item.key} (${blob.size} bytes)`);
      item.resolve(blob);
    } catch {
      item.resolve(null);
    } finally {
      this.active -= 1;
      this.pending.delete(item.key);
      this.processQueue();
    }
  }

  private store(key: string, blob: Blob): void {
    // Both budgets apply: entry count (many small items) and bytes (few huge
    // ones). The incoming blob always lands — evicting everything else to
    // make room for the moment about to be viewed is the right trade.
    while (
      this.cache.size > 0 &&
      (this.cache.size >= MAX_ENTRIES ||
        this.cacheBytes + blob.size > BYTE_BUDGET)
    ) {
      const oldest = this.cache.keys().next().value as string | undefined;
      if (oldest === undefined) break;
      this.drop(oldest);
    }
    this.cache.set(key, { blob, timestamp: Date.now() });
    this.cacheBytes += blob.size;
  }
}

export const momentPrefetcher = new MomentPrefetcherService();
